/**
 * LinkedIn OAuth-start helper — the single place any UI path can call
 * to hand off to LinkedIn's authorize screen.
 *
 * Rules (mirror the spec):
 *  • Calls the existing endpoint `GET /connections/linkedin/oauth/start`.
 *  • Never disconnects, deletes rows, or clears tokens BEFORE the
 *    OAuth callback succeeds. Only the backend callback rotates the
 *    stored token.
 *  • If the backend returns 409 (once-per-cycle platform-change gate),
 *    surfaces the safe payload so the caller can render the existing
 *    confirmation UI. All other errors are surfaced as `code="error"`.
 *  • Never logs, prints, or returns the OAuth `state` value, the
 *    auth URL query string, or any header — only the whitelisted
 *    fields (`code`, `platform`, `human` message).
 *
 * Consumers:
 *   1. The expired-state "Reconnect LinkedIn" button on the
 *      Connections card (bypasses the Manage dialog entirely).
 *   2. The primary CTA inside CustomerConnectionDialog.
 *
 * By centralising here we guarantee both paths carry identical
 * 409-handling, identical error toasts, and identical navigation.
 */
import api from "@/lib/api";

/**
 * Kick off the LinkedIn OAuth flow.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.confirmConsume]  set to `true` after the user
 *   has explicitly confirmed a platform-change on a 409 handshake.
 * @returns {Promise<{ok: boolean, code?: string, human?: string,
 *                     platform?: string}>}
 *   `{ok: true}` never resolves visibly — the browser has already
 *   navigated to LinkedIn. On failure, returns a small structured
 *   payload the caller can render.
 */
export async function startLinkedInOAuth(opts = {}) {
  try {
    const params = [];
    if (opts && opts.confirmConsume) params.push("confirm_consume=true");
    const url =
      "/connections/linkedin/oauth/start" +
      (params.length ? `?${params.join("&")}` : "");
    const res = await api.get(url);
    const authUrl = res && res.data && res.data.auth_url;
    if (typeof authUrl === "string" && authUrl.length > 0) {
      // Hand off to LinkedIn. No token / state values are read from
      // this URL — the browser just navigates.
      window.location.href = authUrl;
      return { ok: true };
    }
    return { ok: false, code: "missing_auth_url" };
  } catch (e) {
    // Preserve the 409 platform-change confirm handshake so callers
    // can render the existing platform-change confirmation UI.
    const status = e && e.response && e.response.status;
    const data = (e && e.response && e.response.data) || {};
    const detail =
      data && typeof data === "object" && "detail" in data
        ? data.detail
        : data;
    const blockedBy =
      detail && typeof detail === "object" ? detail.blocked_by : null;
    if (status === 409 && blockedBy) {
      return {
        ok: false,
        code: blockedBy, // "platform_change_confirm" | "platform_change_limit"
        detail,
        platform: "linkedin",
      };
    }
    return {
      ok: false,
      code: "error",
      detail,
      human:
        (typeof detail === "string" && detail) ||
        (detail && typeof detail === "object" && (detail.human || detail.message)) ||
        "We couldn't start the LinkedIn connection. Please try again.",
    };
  }
}

export default startLinkedInOAuth;
